import { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchUsers } from '../../api/admin';
import { fetchAllStudents } from '../../api/students';
import {
  fetchTutorSchedules,
  createScheduleSlot,
  updateScheduleSlot,
  deleteScheduleSlot,
} from '../../api/schedules';
import { fetchTutorWeeklyReport } from '../../api/weeklyReports';
import WeeklyCalendar from '../../components/calendar/WeeklyCalendar';
import ScheduleFormDialog from '../../components/calendar/ScheduleFormDialog';
import WeeklyReportPanel from '../../components/calendar/WeeklyReportPanel';
import WeekSelector from '../../components/calendar/WeekSelector';
import { ROLES } from '../../utils/roles';
import { enqueueSnackbar } from 'notistack';
import ConfirmDialog from '../../components/feedback/ConfirmDialog';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { formatWeekRangeLabel, getWeekStartDate } from '../../utils/week';

const AdminSchedulePage = () => {
  const queryClient = useQueryClient();
  const normalizeWeek = (value) => getWeekStartDate(value).format('YYYY-MM-DD');
  const [weekStart, setWeekStart] = useState(normalizeWeek());
  const [selectedTutor, setSelectedTutor] = useState('');
  const [dialogState, setDialogState] = useState({ open: false, initialValues: null });
  const [isCopying, setIsCopying] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const scheduleQueryKey = useMemo(
    () => ['schedule', selectedTutor || null, weekStart],
    [selectedTutor, weekStart]
  );

  const usersQuery = useQuery({ queryKey: ['users'], queryFn: fetchUsers, staleTime: 5 * 60 * 1000 });
  const tutors = useMemo(
    () => (usersQuery.data || []).filter((user) => user.role === ROLES.TUTOR),
    [usersQuery.data]
  );

  const studentsQuery = useQuery({ queryKey: ['students'], queryFn: fetchAllStudents, staleTime: 5 * 60 * 1000 });

  const scheduleQuery = useQuery({
    queryKey: scheduleQueryKey,
    queryFn: () => fetchTutorSchedules(Number(selectedTutor), weekStart),
    enabled: Boolean(selectedTutor),
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });

  const weeklyReportQuery = useQuery({
    queryKey: ['weekly-report', selectedTutor, weekStart],
    queryFn: () => fetchTutorWeeklyReport(Number(selectedTutor), weekStart),
    enabled: Boolean(selectedTutor),
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });

  const weeklyStats = useMemo(() => {
    const stats = {
      completed: 0,
      rescheduled: 0,
      cancelled: 0,
      plannedCompleted: 0,
      plannedStart: scheduleQuery.data?.length || 0,
    };
    (scheduleQuery.data || []).forEach((item) => {
      if (item.status === 'COMPLETED') {
        stats.completed += 1;
        stats.plannedCompleted += 1;
      }
      if (item.status === 'RESCHEDULED') stats.rescheduled += 1;
      if (item.status === 'CANCELLED') stats.cancelled += 1;
    });
    return stats;
  }, [scheduleQuery.data]);

  const createMutation = useMutation({
    mutationFn: createScheduleSlot,
    onSuccess: () => {
      enqueueSnackbar('Заняття додано', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
    },
    onError: () => enqueueSnackbar('Не вдалося додати заняття', { variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateScheduleSlot(id, payload),
    onSuccess: () => {
      enqueueSnackbar('Заняття оновлено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
    },
    onError: () => enqueueSnackbar('Не вдалося оновити заняття', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteScheduleSlot,
    onSuccess: () => {
      enqueueSnackbar('Заняття видалено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
    },
    onError: () => enqueueSnackbar('Не вдалося видалити заняття', { variant: 'error' }),
  });

  const handleCellClick = (datetime) => {
    if (!selectedTutor) return;
    const students = (studentsQuery.data || []).filter(
      (student) => String(student.tutorId) === String(selectedTutor)
    );
    setDialogState({
      open: true,
      initialValues: {
        tutorId: Number(selectedTutor),
        studentId: students[0]?.id || '',
        startTime: dayjs(datetime).toISOString(),
        endTime: dayjs(datetime).add(1, 'hour').toISOString(),
        status: 'SCHEDULED',
        comment: '',
      },
    });
  };

  const handleEventClick = (event) => {
    setDialogState({
      open: true,
      initialValues: {
        tutorId: event.tutorId,
        studentId: event.studentId,
        startTime: dayjs(event.startTime).toISOString(),
        endTime: dayjs(event.endTime).toISOString(),
        status: event.status,
        comment: event.comment || '',
        id: event.id,
      },
    });
  };

  const handleDialogSubmit = async (values) => {
    if (dialogState.initialValues?.id) {
      await updateMutation.mutateAsync({ id: dialogState.initialValues.id, payload: values });
    } else {
      await createMutation.mutateAsync(values);
    }
  };

  const currentTutorStudents = useMemo(() => {
    const students = studentsQuery.data || [];
    if (!dialogState.initialValues?.id) {
      return students.filter((student) => String(student.tutorId) === String(selectedTutor));
    }
    return students.filter((student) => {
      if (String(student.tutorId) === String(selectedTutor)) {
        return true;
      }
      return dialogState.initialValues?.studentId === student.id;
    });
  }, [studentsQuery.data, selectedTutor, dialogState.initialValues]);

  const handleDeleteSchedule = async (id) => {
    await deleteMutation.mutateAsync(id);
    setDialogState({ open: false, initialValues: null });
  };

  const handleEventMove = async (event, newStart, newEnd) => {
    if (!selectedTutor) return;
    const previousData = queryClient.getQueryData(scheduleQueryKey);
    const startTime = newStart.format('YYYY-MM-DDTHH:mm:ss');
    const endTime = newEnd.format('YYYY-MM-DDTHH:mm:ss');
    queryClient.setQueryData(scheduleQueryKey, (current) => {
      if (!current) return current;
      return current.map((item) => (item.id === event.id ? { ...item, startTime, endTime } : item));
    });
    try {
      await updateMutation.mutateAsync({
        id: event.id,
        payload: { startTime, endTime },
      });
    } catch (error) {
      queryClient.setQueryData(scheduleQueryKey, previousData);
      throw error;
    }
  };

  const handleCopyPreviousWeek = async () => {
    if (!selectedTutor || isCopying) {
      if (!selectedTutor) {
        enqueueSnackbar('Оберіть репетитора, щоб копіювати розклад', { variant: 'warning' });
      }
      return;
    }
    setIsCopying(true);
    try {
      const previousWeekStart = normalizeWeek(dayjs(weekStart).subtract(1, 'week'));
      const previousSchedules = await fetchTutorSchedules(
        Number(selectedTutor),
        previousWeekStart
      );
      if (!previousSchedules.length) {
        enqueueSnackbar('Немає занять за попередній тиждень', { variant: 'info' });
        return;
      }
      const currentWeekEvents = scheduleQuery.data || [];
      const existingKeys = new Set(
        currentWeekEvents.map(
          (item) => `${item.studentId}-${dayjs(item.startTime).format('YYYY-MM-DDTHH:mm:ss')}`
        )
      );
      const payloads = previousSchedules
        .map((item) => {
          const start = dayjs(item.startTime).add(1, 'week');
          const end = dayjs(item.endTime).add(1, 'week');
          const startFormatted = start.format('YYYY-MM-DDTHH:mm:ss');
          return {
            tutorId: item.tutorId,
            studentId: item.studentId,
            startTime: startFormatted,
            endTime: end.format('YYYY-MM-DDTHH:mm:ss'),
            status: 'SCHEDULED',
            comment: item.comment || '',
            key: `${item.studentId}-${startFormatted}`,
          };
        })
        .filter((payload) => !existingKeys.has(payload.key));

      if (!payloads.length) {
        enqueueSnackbar('Поточний тиждень вже містить ці заняття', { variant: 'info' });
        return;
      }

      await Promise.all(
        payloads.map(({ key, ...payload }) => createScheduleSlot(payload))
      );
      enqueueSnackbar('Заняття скопійовані з попереднього тижня', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
    } catch (error) {
      enqueueSnackbar('Не вдалося скопіювати розклад', { variant: 'error' });
    } finally {
      setIsCopying(false);
    }
  };

  const handleClearCurrentWeek = () => {
    if (!selectedTutor || isClearing) {
      if (!selectedTutor) {
        enqueueSnackbar('Оберіть репетитора, щоб видалити розклад', { variant: 'warning' });
      }
      return;
    }
    const currentWeekEvents = scheduleQuery.data || [];
    if (!currentWeekEvents.length) {
      enqueueSnackbar('За цей тиждень немає занять', { variant: 'info' });
      return;
    }
    setClearConfirmOpen(true);
  };

  const handleConfirmClear = async () => {
    setClearConfirmOpen(false);
    setIsClearing(true);
    try {
      const currentWeekEvents = scheduleQuery.data || [];
      await Promise.all(currentWeekEvents.map((event) => deleteScheduleSlot(event.id)));
      enqueueSnackbar('Заняття тижня видалено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
    } catch (error) {
      enqueueSnackbar('Не вдалося видалити заняття', { variant: 'error' });
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <TextField
                label="Репетитор"
                select
                size="small"
                value={selectedTutor}
                onChange={(e) => setSelectedTutor(e.target.value)}
                sx={{ width: { xs: '100%', md: 240 } }}
              >
                <MenuItem value="">Оберіть репетитора</MenuItem>
                {tutors.map((tutor) => (
                  <MenuItem key={tutor.id} value={tutor.id}>
                    {tutor.name}
                  </MenuItem>
                ))}
              </TextField>
              <Tooltip
                title={
                  selectedTutor
                    ? 'Скопіювати з попереднього тижня'
                    : 'Оберіть репетитора, щоб копіювати розклад'
                }
              >
                <span>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={handleCopyPreviousWeek}
                    disabled={!selectedTutor || isCopying}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  selectedTutor
                    ? 'Видалити всі заняття поточного тижня'
                    : 'Оберіть репетитора, щоб видалити розклад'
                }
              >
                <span>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={handleClearCurrentWeek}
                    disabled={!selectedTutor || isClearing}
                  >
                    <DeleteSweepIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <WeekSelector value={weekStart} onChange={setWeekStart} />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flexWrap: 'nowrap', gap: 1, minWidth: 0 }}
            >
              <IconButton
                color="primary"
                aria-label="Попередній тиждень"
                onClick={() => setWeekStart(normalizeWeek(dayjs(weekStart).subtract(1, 'week')))}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Button
                variant="outlined"
                onClick={() => setWeekStart(normalizeWeek(dayjs()))}
                sx={{ minWidth: 160 }}
              >
                Поточний тиждень
              </Button>
              <IconButton
                color="primary"
                aria-label="Наступний тиждень"
                onClick={() => setWeekStart(normalizeWeek(dayjs(weekStart).add(1, 'week')))}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {!selectedTutor ? (
            <Box sx={{ py: 10, textAlign: 'center' }}>Оберіть репетитора для перегляду розкладу</Box>
          ) : scheduleQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <WeeklyCalendar
              weekStart={weekStart}
              events={scheduleQuery.data}
              onCellClick={handleCellClick}
              onEventClick={handleEventClick}
              onEventMove={handleEventMove}
              showAuditInfo
            />
          )}
        </CardContent>
      </Card>

      {selectedTutor && (
        <WeeklyReportPanel
          title="Тижневий стан викладача"
          subtitle={`Тиждень ${formatWeekRangeLabel(weekStart)}`}
          values={weeklyReportQuery.data ?? null}
          readOnly
          emptyStateMessage="Репетитор ще не заповнив звіт за цей тиждень"
          updatedAt={weeklyReportQuery.data?.updatedAt}
          studentOptions={currentTutorStudents}
        />
      )}

      <Card>
        <CardContent>
          {!selectedTutor ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>Оберіть репетитора, щоб побачити статистику</Box>
          ) : (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box
                sx={{
                  flex: 1,
                  border: '1px solid #E2E8F0',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: '#EEF2FF',
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Запланованих уроків
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {Math.max(weeklyStats.plannedStart - weeklyStats.rescheduled, 0)}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  border: '1px solid #E2E8F0',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: '#ECFDF5',
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Проведених уроків
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {weeklyStats.completed}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  border: '1px solid #E2E8F0',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: '#FEF3C7',
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Перенесених уроків
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {weeklyStats.rescheduled}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  border: '1px solid #E2E8F0',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: '#FEE2E2',
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Скасованих уроків
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {weeklyStats.cancelled}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  border: '1px solid #E2E8F0',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: '#E0F2FE',
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Запланованих → проведених
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {weeklyStats.plannedCompleted}
                </Typography>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={clearConfirmOpen}
        title="Видалити всі заняття тижня?"
        description="Усі заняття поточного тижня буде видалено. Цю дію відмінити неможливо."
        confirmText="Видалити"
        onCancel={() => setClearConfirmOpen(false)}
        onConfirm={handleConfirmClear}
      />

      <ScheduleFormDialog
        open={dialogState.open}
        onClose={() => setDialogState({ open: false, initialValues: null })}
        onSubmit={handleDialogSubmit}
        onDelete={
          dialogState.initialValues?.id
            ? () => handleDeleteSchedule(dialogState.initialValues.id)
            : undefined
        }
        tutors={tutors}
        students={currentTutorStudents}
        initialValues={dialogState.initialValues}
        enableTutorSelect
        title={dialogState.initialValues?.id ? 'Редагувати заняття' : 'Нове заняття'}
      />
    </Stack>
  );
};

export default AdminSchedulePage;

